import React, { useEffect, useMemo, useState } from 'react';
import { MainLayout } from '../MainLayout';
import { supabase } from '../../lib/supabase_client';
import '../../styles/page-base.css';

interface SalesmanRow {
  id: string;
  code: string;
  full_name: string;
}

interface OrgPromoRow {
  id: string;
  name: string;
  country_iso: string;
  promo_code: string | null;
  created_at: string;
  salesman_full_name: string | null;
  first_purchased_at: string | null;
}

function normalizeCode(s: string | null | undefined): string {
  return (s ?? '').trim().toLowerCase();
}

export default function AdminSalesman() {
  const [salesmen, setSalesmen] = useState<SalesmanRow[]>([]);
  const [orgs, setOrgs] = useState<OrgPromoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [promoFilter, setPromoFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const { data: smRows, error: smErr } = await supabase
          .from('salesman')
          .select('id, code, full_name')
          .order('code', { ascending: true });

        if (cancelled) return;
        if (smErr) {
          console.error('AdminSalesman: salesman fetch', smErr);
          setSalesmen([]);
        } else {
          setSalesmen(smRows ?? []);
        }

        const map = new Map<string, string>();
        for (const s of smRows ?? []) {
          map.set(normalizeCode(s.code), s.full_name);
        }

        const BATCH = 1000;
        let offset = 0;
        const all: {
          id: string;
          name: string;
          country_iso: string;
          promo_code: string | null;
          created_at: string;
        }[] = [];

        let hasMore = true;
        while (hasMore && !cancelled) {
          const { data, error } = await supabase
            .from('organizations')
            .select('id, name, country_iso, promo_code, created_at')
            .not('promo_code', 'is', null)
            .order('created_at', { ascending: false })
            .range(offset, offset + BATCH - 1);

          if (cancelled) return;
          if (error) {
            console.error('AdminSalesman: organizations fetch', error);
            setOrgs([]);
            return;
          }
          if (!data?.length) {
            hasMore = false;
            break;
          }
          all.push(...data);
          offset += BATCH;
          hasMore = data.length === BATCH;
        }

        if (cancelled) return;

        const enriched: OrgPromoRow[] = all
          .filter((o) => (o.promo_code ?? '').trim() !== '')
          .map((o) => ({
            ...o,
            salesman_full_name: map.get(normalizeCode(o.promo_code)) ?? null,
            first_purchased_at: null,
          }));

        const orgIds = enriched.map((o) => o.id);
        const firstPurchaseMap = new Map<string, string>();
        if (orgIds.length > 0) {
          const { data: purchases, error: purchaseErr } = await supabase
            .from('org_purchases')
            .select('org_id, created_at')
            .in('org_id', orgIds)
            .eq('status', 'paid')
            .order('created_at', { ascending: true });
          if (purchaseErr) {
            console.error('AdminSalesman: org_purchases fetch', purchaseErr);
          } else {
            for (const p of purchases ?? []) {
              if (!firstPurchaseMap.has(p.org_id)) {
                firstPurchaseMap.set(p.org_id, p.created_at);
              }
            }
          }
        }
        const enrichedWithFirstPurchase: OrgPromoRow[] = enriched.map((o) => ({
          ...o,
          first_purchased_at: firstPurchaseMap.get(o.id) ?? null,
        }));
        setOrgs(enrichedWithFirstPurchase);

      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredRows = useMemo(() => {
    return orgs.filter((row) => {
      if (promoFilter) {
        if (normalizeCode(row.promo_code) !== normalizeCode(promoFilter)) return false;
      }
      if (dateFrom) {
        const start = new Date(dateFrom);
        start.setHours(0, 0, 0, 0);
        if (new Date(row.created_at) < start) return false;
      }
      if (dateTo) {
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        if (new Date(row.created_at) > end) return false;
      }
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        if (
          !row.name.toLowerCase().includes(q) &&
          !(row.promo_code ?? '').toLowerCase().includes(q) &&
          !(row.salesman_full_name ?? '').toLowerCase().includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [orgs, promoFilter, dateFrom, dateTo, searchTerm]);

  const totalFiltered = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageRows = filteredRows.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [promoFilter, dateFrom, dateTo, searchTerm, rowsPerPage]);

  const hasActiveFilters = useMemo(
    () => !!promoFilter || !!dateFrom || !!dateTo || searchTerm.trim() !== '',
    [promoFilter, dateFrom, dateTo, searchTerm]
  );

  const clearFilters = () => {
    setPromoFilter('');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  return (
    <MainLayout>
      <div className="page-container">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="page-header">
            <div className="page-header-left">
              <div className="text-sm text-gray-500 mb-1">Admin Only</div>
              <h1 className="page-title">Salesman</h1>
            </div>
          </div>

          {/* Filter Section — layout matches UsageLogs transaction tab */}
          <div className="page-card">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  Promo code
                </label>
                <select
                  value={promoFilter}
                  onChange={(e) => setPromoFilter(e.target.value)}
                  className="block w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All promo codes</option>
                  {salesmen.map((s) => (
                    <option key={s.id} value={s.code}>
                      {s.code}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  From Date
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="block w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  To Date
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="block w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={clearFilters}
                  className="f-btn f-btn-gray"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Search, totals, table — separate page-card (matches UsageLogs) */}
          <div className="page-card">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search company, promo code, salesman..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full text-sm pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="text-sm text-gray-700 whitespace-nowrap flex-shrink-0">
                <span className="font-semibold text-gray-900">{loading ? '…' : orgs.length}</span>
                <span className="text-gray-600 ml-1">
                  Organization{orgs.length !== 1 ? 's' : ''} with Promo
                </span>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
              <table className="page-table">
                <thead className="page-table-thead">
                  <tr className="page-table-tr">
                    <th className="page-table-th">Organization</th>
                    <th className="page-table-th">Country</th>
                    <th className="page-table-th">Promo code</th>
                    <th className="page-table-th">Salesman</th>
                    <th className="page-table-th">Created At</th>
                    <th className="page-table-th">First Purchased At</th>
                  </tr>
                </thead>
                <tbody className="page-table-tbody">
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-gray-500">
                        Loading…
                      </td>
                    </tr>
                  ) : pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-gray-500">
                        No organizations match the filters.
                      </td>
                    </tr>
                  ) : (
                    pageRows.map((row) => (
                      <tr className="page-table-tr" key={row.id}>
                        <td className="page-table-td font-medium text-gray-900">{row.name}</td>
                        <td className="page-table-td">{row.country_iso}</td>
                        <td className="page-table-td font-mono text-sm">{row.promo_code ?? '—'}</td>
                        <td className="page-table-td">
                          {row.salesman_full_name ? (
                            <span className="text-green-800">{row.salesman_full_name}</span>
                          ) : (
                            <span className="text-amber-700 text-sm">No matching salesman</span>
                          )}
                        </td>
                        <td className="page-table-td whitespace-nowrap">
                          {new Date(row.created_at).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="page-table-td whitespace-nowrap">
                          {row.first_purchased_at
                            ? new Date(row.first_purchased_at).toLocaleString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination — matching UsageLogs / BillingHistory */}
            {!loading && filteredRows.length > 0 && (
              <div className="flex items-center justify-between text-sm text-gray-600 mt-6">
                <div className="flex items-center space-x-2">
                  <span>Rows per page:</span>
                  <select
                    value={rowsPerPage}
                    onChange={(e) => setRowsPerPage(Number(e.target.value))}
                    className="border border-gray-300 rounded px-2 py-1"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <span>
                  {startIndex + 1}-{Math.min(endIndex, filteredRows.length)} of {filteredRows.length}
                </span>
                <div className="flex space-x-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                  >
                    &lt;
                  </button>
                  <button type="button" className="f-btn f-btn-primary f-btn-xs">
                    {currentPage}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}